if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.custom-select__btn');
      this.listbox = this.querySelector('.custom-select__listbox');
      this.selectedOption = this.querySelector('[aria-selected="true"]');
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }
      this.setButtonWidth();
      window.initLazyScript(this, this.init.bind(this));
    }
    init() {
      this.options = this.querySelectorAll('.custom-select__option');
      this.nativeSelect = document.getElementById(`${this.id}-native`);
      this.swatches = !!Array.from(this.options).find(el => 'swatch' in el.dataset);
      this.focusedClass = 'is-focused';
      this.searchString = '';
      this.listboxOpen = false;
      this.selectedOption = this.querySelector('[aria-selected="true"]');
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }
      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.button.addEventListener('mousedown', this.handleMousedown.bind(this));
    }
    static getNextVisibleSibling(elem) {
      let sibling = elem.nextElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.nextElementSibling;
      }
      return null;
    }
    static getPreviousVisibleSibling(elem) {
      let sibling = elem.previousElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.previousElementSibling;
      }
      return null;
    }
    addListboxOpenListeners() {
      this.mouseoverHandler = this.handleMouseover.bind(this);
      this.mouseleaveHandler = this.handleMouseleave.bind(this);
      this.clickHandler = this.handleClick.bind(this);
      this.blurHandler = this.handleBlur.bind(this);
      this.listbox.addEventListener('mouseover', this.mouseoverHandler);
      this.listbox.addEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.addEventListener('click', this.clickHandler);
      this.listbox.addEventListener('blur', this.blurHandler);
    }
    removeListboxOpenListeners() {
      this.listbox.removeEventListener('mouseover', this.mouseoverHandler);
      this.listbox.removeEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.removeEventListener('click', this.clickHandler);
      this.listbox.removeEventListener('blur', this.blurHandler);
    }
    handleKeydown(evt) {
      if (this.listboxOpen) {
        this.handleKeyboardNav(evt);
      }
      else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ') {
        evt.preventDefault();
        this.showListbox();
      }
    }
    handleMousedown(evt) {
      if (!this.listboxOpen && evt.button === 0) {
        this.showListbox();
      }
    }
    handleMouseover(evt) {
      if (evt.target.matches('li')) {
        this.focusOption(evt.target);
      }
    }
    handleMouseleave() {
      this.focusOption(this.selectedOption);
    }
    handleClick(evt) {
      if (evt.target.tagName === 'LI') {
        this.selectOption(evt.target);
      }
    }
    handleBlur() {
      if (this.listboxOpen) {
        this.hideListbox();
      }
    }
    handleKeyboardNav(evt) {
      let optionToFocus;
      if (evt.key === 'Tab') {
        evt.preventDefault();
      }
      switch (evt.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        evt.preventDefault();
        if (evt.key === 'ArrowUp') {
          optionToFocus = CustomSelect.getPreviousVisibleSibling(this.focusedOption);
        }
        else {
          optionToFocus = CustomSelect.getNextVisibleSibling(this.focusedOption);
        }
        if (optionToFocus && !optionToFocus.classList.contains('is-disabled')) {
          this.focusOption(optionToFocus);
        }
        break;
      case 'Enter':
      case ' ':
        evt.preventDefault();
        this.selectOption(this.focusedOption);
        break;
      case 'Escape':
        evt.preventDefault();
        this.hideListbox();
        break;
      default:
        optionToFocus = this.findOption(evt.key);
        if (optionToFocus) {
          this.focusOption(optionToFocus);
        }
        break;
      }
    }
    setButtonWidth() {
      const getHorizontalPadding = (el) => {
        const elStyle = getComputedStyle(el);
        return parseFloat(elStyle.paddingLeft) + parseFloat(elStyle.paddingRight);
      };
      const buttonPadding = getHorizontalPadding(this.button);
      const optionPadding = getHorizontalPadding(this.selectedOption);
      const buttonBorder = this.button.offsetWidth - this.button.clientWidth;
      const optionWidth = Math.ceil(this.selectedOption.getBoundingClientRect().width);
      this.button.style.setProperty('--custom-select-button-width', `${optionWidth - optionPadding + buttonPadding + buttonBorder}px`);
    }
    showListbox() {
      this.listbox.hidden = false;
      this.listboxOpen = true;
      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        this.focusOption(this.selectedOption);
        this.listbox.focus();
        this.addListboxOpenListeners();
      });
    }
    hideListbox() {
      if (!this.listboxOpen) return;
      this.listbox.hidden = true;
      this.listboxOpen = false;
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');
      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
        this.focusedOption = null;
      }
      this.button.focus();
      this.removeListboxOpenListeners();
    }
    findOption(key) {
      this.searchString += key;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.searchString = '';
      }, 500);
      const matchingOption = Array.from(this.options).find((option) => {
        const label = option.innerText.toLowerCase();
        return label.includes(this.searchString) && !option.classList.contains('is-disabled');
      });
      return matchingOption;
    }
    focusOption(option) {
      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
      }
      this.focusedOption = option;
      this.focusedOption.classList.add(this.focusedClass);
      if (this.listbox.scrollHeight > this.listbox.clientHeight) {
        const scrollBottom = this.listbox.clientHeight + this.listbox.scrollTop;
        const optionBottom = option.offsetTop + option.offsetHeight;
        if (optionBottom > scrollBottom) {
          this.listbox.scrollTop = optionBottom - this.listbox.clientHeight;
        }
        else if (option.offsetTop < this.listbox.scrollTop) {
          this.listbox.scrollTop = option.offsetTop;
        }
      }
    }
    selectOption(option) {
      if (option !== this.selectedOption) {
        option.setAttribute('aria-selected', 'true');
        this.selectedOption.setAttribute('aria-selected', 'false');
        if (this.swatches) {
          this.button.dataset.swatch = option.dataset.swatch || '';
          const nativeSwatchColor = option.style.getPropertyValue('--native-swatch-color');
          if (nativeSwatchColor) {
            this.button.style.setProperty('--native-swatch-color', nativeSwatchColor);
            this.button.style.setProperty('--native-swatch-image', '');
          }
          const nativeSwatchImage = option.style.getPropertyValue('--native-swatch-image');
          if (nativeSwatchImage) {
            this.button.style.setProperty('--native-swatch-image', nativeSwatchImage);
            this.button.style.setProperty('--native-swatch-color', '');
          }
        }
        this.button.firstElementChild.textContent = option.firstElementChild.textContent;
        this.listbox.setAttribute('aria-activedescendant', option.id);
        this.selectedOption = document.getElementById(option.id);
        if (this.nativeSelect) {
          this.nativeSelect.value = option.dataset.value;
          this.nativeSelect.dispatchEvent(
            new Event('change', {
              bubbles: true,
            }),
          );
        }
        else {
          const detail = {
            selectedValue: option.dataset.value,
          };
          this.dispatchEvent(
            new CustomEvent('change', {
              bubbles: true,
              detail,
            }),
          );
        }
      }
      this.hideListbox();
    }
  }
  customElements.define('custom-select', CustomSelect);
}
//# sourceMappingURL=custom-select.js.map
